//#region src/contract/types.ts
/**
* Kinds of request input a route can declare a schema for.
*/
const INPUT_KINDS = [
	"query",
	"path",
	"headers",
	"body"
];
/**
* Fills the `{param}` placeholders of a path template and appends any remaining params as a query string.
*
* Serialization is intentionally minimal: `undefined`/`null` query values are skipped, arrays append
* repeated keys, `Date` values become ISO strings, and everything else is stringified.
* @param template path template, e.g. `/v1/answers/{id}`
* @param params path and query parameter values
* @returns the resolved path, e.g. `/v1/answers/123?expand=true`
*/
function buildPath(template, params = {}) {
	const used = /* @__PURE__ */ new Set();
	const path = template.replaceAll(/\{(\w+)\}/g, (_match, name) => {
		const value = params[name];
		if (value === void 0 || value === null) throw new Error(`Missing path parameter "${name}" for "${template}"`);
		used.add(name);
		return encodeURIComponent(String(value));
	});
	const query = new URLSearchParams();
	for (const [key, value] of Object.entries(params)) {
		if (used.has(key) || value === void 0 || value === null) continue;
		for (const entry of Array.isArray(value) ? value : [value]) query.append(key, entry instanceof Date ? entry.toISOString() : String(entry));
	}
	const search = query.toString();
	return search ? `${path}?${search}` : path;
}
/**
* Joins a base URL and a path, preserving any path already present on the base.
*/
function joinUrl(base, path) {
	const prefix = (base instanceof URL ? base.href : base).replace(/\/+$/, "");
	return new URL(`${prefix}/${path.replace(/^\/+/, "")}`);
}

//#endregion
//#region src/contract/response-examples.ts
/**
* Meta `examples` of a schema, normalized to an array. Supports both the array form
* and the JSON-Schema / OpenAPI object form (`{ name: { value: ... } }`).
*/
function metaExamples(schema) {
	const examples = schema.meta()?.examples;
	if (Array.isArray(examples)) return examples;
	if (examples && typeof examples === "object") return Object.values(examples).map((entry) => entry && typeof entry === "object" && "value" in entry ? entry.value : void 0).filter((value) => value !== void 0);
	return [];
}
/**
* Assert that every declared response schema of a contract carries at least one
* schema-valid meta example, throwing a single error listing all offenders. Schemas
* that accept `undefined` (bodyless 204s, `z.unknown()` passthroughs) are exempt —
* `undefined` is their implicit example.
*
* Call from a contract test:
* `it("declares valid response examples", () => assertResponseExamples(Contract))`.
*/
function assertResponseExamples(contract) {
	const problems = [];
	for (const [routerId, apiRouter] of Object.entries(contract.def.routers)) for (const [routeId, route] of Object.entries(apiRouter.def.routes)) {
		const label = `${routerId}.${routeId} (${route.def.method} ${apiRouter.def.prefix}${route.def.path})`;
		for (const [status, schema] of Object.entries(route.def.outputs)) {
			if (schema.safeParse(void 0).success) continue;
			const examples = metaExamples(schema);
			if (examples.length === 0) {
				problems.push(`${label} ${status}: no meta examples — declare .meta({ examples: [...] }) on the response schema`);
				continue;
			}
			examples.forEach((example, index) => {
				const parsed = schema.safeParse(example);
				if (!parsed.success) problems.push(`${label} ${status}: meta example ${index} does not match the schema — ${parsed.error.message}`);
			});
		}
	}
	if (problems.length > 0) throw new Error(`Response schemas without valid meta examples:\n${problems.join("\n")}`);
}

//#endregion
export { joinUrl as a, buildPath as i, metaExamples as n, INPUT_KINDS as r, assertResponseExamples as t };